/** Copyright (c) 2019 T. Rowe Price. All rights reserved. */

import './PaperlessPreferences.scss';

import { bool, object } from 'prop-types';
import React, { Fragment, useEffect, useState } from 'react';
import { isEmpty, uniqBy, omit } from 'lodash';
import { withRouter, Redirect } from 'react-router-dom';
import { connect } from 'react-redux';
import Store from 'state/Store';
import isEqual from 'lodash/isEqual';
import { usePrevious } from 'support/ReactUtils';
import { AAMBeaconControl } from 'components/shared/Beacon';
import PreferencesHeader from 'components/layouts/Preferences/PreferencesHeader';
import PreferencesRow from 'components/layouts/Preferences/shared/PreferencesRow';
import PreferencesSection from 'components/layouts/Preferences/shared/PreferencesSection';
import PaperlessPreferenceSection from './PaperlessPreferenceSection';
import {
  getPaperlessPreferences,
  getEligibleServiceTypeIds,
  getPaperlessPreferencesOverview,
  getUpdatedDocumentDeliveryEnrollments
} from 'state/preferences/PaperlessPreferencesSelectors';
import {
  getHasOwnedCollegeSavingsOnly,
  hasOwnedBrokerage,
  hasOwnedMutualFunds
} from 'state/account/AccountSelectors';
import { getHasInvalidOrNoEmailAddress } from 'state/contactMethodMaintenance/ContactMethodMaintenanceSelectors';
import {
  getCustomerDocumentDelivery,
  updateDocumentDelivery
} from 'services/Rest';
import {
  errorPaperlessDisclosure,
  hideFixedFooterSaveOptions,
  receivedPaperlessDisclosure,
  requestedPaperlessDisclosure,
  showFixedFooterSaveOptions
} from 'state/preferences/PreferencesActions';
import PaperlessPreferencesAccountGroup from './PaperlessPreferencesAccountGroup';
import { getRequestStatus } from 'state/requests/RequestStatusSelectors';
import {
  generalGroupIdMap,
  YE_PAPER_STATEMENTS_SERVICE_TYPE_ID,
  mutualFundsNote,
  BrokerageNote
} from 'services/Preferences';
import FixedSubmitFooter from '../shared/FixedSubmitFooter/FixedSubmitFooter';
import Content, {
  Registered,
  TradeMark
} from 'services/Content';
import PaperlessAccordionWithFootNotes from './PaperlessAccordionWithFootNotes';
import AllPaperless from './PaperlessBanner/AllPaperless';
import GoPaperless from './PaperlessBanner/GoPaperless';
import { AEM, feesAndMinimumsUrl } from 'services/Externals';
import PreferencesInvalidEmail from './PreferencesInvalidEmail';
import PaperlessPreferencesOverview from './PaperlessOverview/PaperlessPreferencesOverview';
import ConsentModal from 'components/layouts/Preferences/PaperlessPreferences/ConsentModal';
import { getPrimaryEmailAddress } from 'state/customer/CustomerSelectors';
import { isSalesforceRepEnabled } from 'state/view/ViewSelectors';
import {
  CONTENT_TYPES,
  getContentFromCMS
} from 'services/ContentManagement/CMSUtils';
import {
  CONFIRMATIONS_SERVICE_TYPE_ID,
  PROSPECTUS_SERVICE_TYPE_ID,
  STATEMENTS_SERVICE_TYPE_ID
} from 'services/PaperlessPreferences';
import { hasAllInvestingRows } from 'components/shared/GroupedAccountList/util';


const PaperlessPreferences = ({
  history,
  hasInvalidOrMissingEmail,
  paperlessPreferencesSettings,
  hasOwnedBrokerage,
  hasOwnedMutualFunds,
  documentDelivery,
  requestStatus,
  hideFixedFooter,
  eligibleMFServiceTypeIds,
  location,
  paperlessPreferencesStatus,
  emailAddress,
  isSalesforceRepEnabled,
  hasOwnedCollegeSavingsOnly
}) => {
  const [hasError, setHasError] = useState(false);
  const [termsContent, setTermsContent] = useState(null);
  const [loadedPaperlessDelivery, setLoadedPaperlessDelivery] = useState();
  const [paperlessDelivery, setPaperlessDelivery] = useState();
  const previousDocumentDelivery = usePrevious(documentDelivery);
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [hideConsentActionZone, setHideConsentActionZone] = useState(false);

  useEffect(() => {
    Store.dispatch(requestedPaperlessDisclosure());
    getContentFromCMS(
      AEM.FRAGMENTS.paperlessDisclosure,
      'main',
      CONTENT_TYPES.HTML,
      (root) => {
        return root['value'];
      },
      () => setHasError(true)
    )
      .then((content) => {
        if (!isEmpty(content)) {
          Store.dispatch(receivedPaperlessDisclosure());
          setTermsContent(content);
        } else {
          Store.dispatch(errorPaperlessDisclosure());
          setHasError(true);
        }
      })
      .catch(() => {
        Store.dispatch(errorPaperlessDisclosure());
        setHasError(true);
      });

    getCustomerDocumentDelivery();
    Store.dispatch(hideFixedFooterSaveOptions());
  }, []);

  useEffect(() => {
    if (
      !loadedPaperlessDelivery ||
      !isEqual(previousDocumentDelivery, documentDelivery)
    ) {
      setPaperlessDelivery(documentDelivery);
      setLoadedPaperlessDelivery(documentDelivery);
    }

    return () => {};
  }, [documentDelivery]);

  if (hasError) {
    return (
      <Redirect
        to={{
          pathname: '/error',
          state: { from: location }
        }}
      />
    );
  }

  const updateDeliverySelection = (event, serviceTypeId) => {
    const isEmailDelivery = event.target.value === 'email';

    var newPaperlessDelivery = {};

    if (Array.isArray(serviceTypeId)) {
      serviceTypeId.forEach(
        (id) => (newPaperlessDelivery[id] = isEmailDelivery)
      );
    } else {
      newPaperlessDelivery[serviceTypeId] = isEmailDelivery;
    }

    if ( hasOwnedMutualFunds && serviceTypeId === CONFIRMATIONS_SERVICE_TYPE_ID && isEmailDelivery) {
      newPaperlessDelivery[PROSPECTUS_SERVICE_TYPE_ID] = true;
    }

    if (serviceTypeId === PROSPECTUS_SERVICE_TYPE_ID && !isEmailDelivery) {
      newPaperlessDelivery[CONFIRMATIONS_SERVICE_TYPE_ID] = false;
    }

    if (
      serviceTypeId === STATEMENTS_SERVICE_TYPE_ID &&
      isSalesforceRepEnabled
    ) {
      if (newPaperlessDelivery[STATEMENTS_SERVICE_TYPE_ID] === false) {
        newPaperlessDelivery[YE_PAPER_STATEMENTS_SERVICE_TYPE_ID] = false;
      } else if (newPaperlessDelivery[STATEMENTS_SERVICE_TYPE_ID] === true) {
        newPaperlessDelivery[YE_PAPER_STATEMENTS_SERVICE_TYPE_ID] =
          loadedPaperlessDelivery[YE_PAPER_STATEMENTS_SERVICE_TYPE_ID];
      }
    }
    setPaperlessDelivery((prevState) => {
      return {
        ...prevState,
        ...newPaperlessDelivery
      };
    });

    showSaveOptions();
  };

  const handleCancel = () => {
    history.push('/profile');
  };

  const showSaveOptions = () => {
    Store.dispatch(showFixedFooterSaveOptions());
  };

  const shouldShowConsentModal = (complianceSubscriptions = []) => {
    if (
      !isSalesforceRepEnabled &&
      complianceSubscriptions.some(
        (subscription) => subscription.subscribed === true
      )
    ) {
      return true;
    }

    return false;
  };

  const handleSave = () => {
    const complianceSubscriptions = Object.keys(paperlessDelivery)
      .filter(subscription => paperlessDelivery[subscription] !== loadedPaperlessDelivery[subscription])
      .map(serviceTypeId => {
        return {
          subscriptionId: Number(serviceTypeId),
          subscribed: Boolean(paperlessDelivery[serviceTypeId])}
        }
      );

    if (
      shouldShowConsentModal(complianceSubscriptions) &&
      showConsentModal === false
    ) {
      setShowConsentModal(true);
      setHideConsentActionZone(false);
      return;
    }

    if (isSalesforceRepEnabled) {
      if (
        paperlessDelivery[YE_PAPER_STATEMENTS_SERVICE_TYPE_ID] !==
        loadedPaperlessDelivery[YE_PAPER_STATEMENTS_SERVICE_TYPE_ID]
      ) {
        complianceSubscriptions.push({
          subscriptionId: Number(YE_PAPER_STATEMENTS_SERVICE_TYPE_ID),
          subscribed: Boolean(
            paperlessDelivery[YE_PAPER_STATEMENTS_SERVICE_TYPE_ID]
          )
        });
      }
    }
    const payload = {
      subscriptions: {
        complianceSubscriptions: uniqBy(complianceSubscriptions, ({ subscriptionId }) => subscriptionId)
      }
    };

    updateDocumentDelivery(payload, emailAddress);
  };

  const checkIfDisableSubmit = () => {
    return !Object.keys(paperlessDelivery).some(
      (id) => paperlessDelivery[id] !== documentDelivery[id]
    );
  };

  const isGeneralGroupAllEmailForMf = () => {
    // While `Proxy Materials` is in the general section it is not part of the concideration for the banner
    return Object.values(generalGroupIdMap).every(
      (serviceTypeId) => paperlessDelivery[serviceTypeId]
    );
  };

  const selectAllGeneralPaperless = () => {
    const generalSelections = Object.values(generalGroupIdMap).reduce(
      (acc, serviceTypeId) => ({ ...acc, [serviceTypeId]: true }),
      {}
    );
    const newDocumentDelivery = {
      ...paperlessDelivery,
      ...generalSelections
    };
    setPaperlessDelivery(newDocumentDelivery);
    showSaveOptions();
  };

  const switchToAllEPaperless = () => {
    const newPaperlessDelivery = {};

    //Filter out RAS and APP for now until those service ID's are ready day 2 enhancement
    const filteredPaperlessPreferencesSettings = omit(paperlessPreferencesSettings, ['activeplus', 'activeadvisory']);
    Object.keys(filteredPaperlessPreferencesSettings).forEach((key) => {
      filteredPaperlessPreferencesSettings[key].map((document) => {
        newPaperlessDelivery[document.serviceTypeId] = true;
      });
    });

    setPaperlessDelivery((prevState) => {
      return {
        ...prevState,
        ...newPaperlessDelivery
      };
    });

    setHideConsentActionZone(false);
    setShowConsentModal(true);
  };

  const updateYEPaperStatements = () => {
    const newDocumentDelivery = {
      ...paperlessDelivery
    };

    newDocumentDelivery[YE_PAPER_STATEMENTS_SERVICE_TYPE_ID] =
      !newDocumentDelivery[YE_PAPER_STATEMENTS_SERVICE_TYPE_ID];

    setPaperlessDelivery((prevState) => ({
      ...prevState,
      ...newDocumentDelivery
    }));
    showSaveOptions();
  };

  const hasBrokeragePreferences =
    paperlessPreferencesSettings &&
    paperlessPreferencesSettings.brokerage &&
    paperlessPreferencesSettings.brokerage.length;

  const hasMutualFundPreferences =
    paperlessPreferencesSettings &&
    paperlessPreferencesSettings.mutualfund &&
    paperlessPreferencesSettings.mutualfund.length;
  const hasNationalSavingsPreferences =
    paperlessPreferencesSettings &&
    paperlessPreferencesSettings.nationalsavings &&
    paperlessPreferencesSettings.nationalsavings.length;

  const hasMarylandSavingsPreferences =
    paperlessPreferencesSettings &&
    paperlessPreferencesSettings.marylandsavings &&
    paperlessPreferencesSettings.marylandsavings.length;

  const hasAlaskaSavingsPreferences =
    paperlessPreferencesSettings &&
    paperlessPreferencesSettings.alaskasavings &&
    paperlessPreferencesSettings.alaskasavings.length;

  const hasActivePlusPreferences =
    paperlessPreferencesSettings &&
    paperlessPreferencesSettings.activeplus &&
    paperlessPreferencesSettings.activeplus.length;

  const hasAdvisoryPreferences =
    paperlessPreferencesSettings &&
    paperlessPreferencesSettings.activeadvisory &&
    paperlessPreferencesSettings.activeadvisory.length;

  const renderCheckbox = () => {

    if (!isSalesforceRepEnabled) {
      return null;
    }

    let note = hasOwnedMutualFunds
      ? mutualFundsNote
      : BrokerageNote;

    return (
      <div className="document-delivery-section-content">
        <div className="document-delivery-section-row">
          <AAMBeaconControl>
            <label slot="label" htmlFor="yepaperstatements-checkbox">
              Year-end Paper Statements
            </label>
            <input type="checkbox"
                   id="yepaperstatements-checkbox"
                   onChange={updateYEPaperStatements}
                   disabled={paperlessDelivery[STATEMENTS_SERVICE_TYPE_ID] === false}
                   checked={paperlessDelivery[YE_PAPER_STATEMENTS_SERVICE_TYPE_ID]} />
          </AAMBeaconControl>
          <span>{note}</span>
        </div>
      </div>
    );
  };

  const PaperlessPreferencesViewSection = () => (
    <PreferencesRow className="paperless-preferences-options-section beacon-on-lighter">
      {hasMutualFundPreferences && (
        <PaperlessPreferenceSection
          accountsSectionTitle={'Mutual Fund Accounts'}>
          {paperlessPreferencesSettings.mutualfund.map(
            ({ modalContent, note, serviceTypeId, showBanner, title }) => {
              return (
                <div
                  className="paperless-account-section-row"
                  key={`mutualfund-${title}`}>
                  <PaperlessPreferencesAccountGroup
                    title={title}
                    isEmailDelivery={paperlessDelivery[serviceTypeId]}
                    disabledRadioGroup={
                      isSalesforceRepEnabled &&
                      !loadedPaperlessDelivery[serviceTypeId]
                    }
                    handleChange={updateDeliverySelection}
                    note={
                      (title === 'Prospectuses & Shareholder Reports' ||
                        hasOwnedBrokerage) &&
                      note
                    }
                    product={'mutualfund'}
                    serviceTypeId={serviceTypeId}
                    modalContent={modalContent}
                    showBanner={showBanner}
                  />
                  {showBanner &&
                    !isSalesforceRepEnabled &&
                    (isGeneralGroupAllEmailForMf() ? (
                      <AllPaperless />
                    ) : (
                      hasAllInvestingRows(eligibleMFServiceTypeIds) && (
                        <GoPaperless
                          selectAllPaperless={selectAllGeneralPaperless}
                        />
                      )
                    ))}
                </div>
              );
            }
          )}
          {renderCheckbox()}
        </PaperlessPreferenceSection>
      )}
      {hasBrokeragePreferences && (
        <PaperlessPreferenceSection accountsSectionTitle={'Brokerage Accounts'}>
          {paperlessPreferencesSettings.brokerage.map(
            ({ modalContent, note, serviceTypeId, showBanner, title }) => {
              return (
                <div
                  className="paperless-account-section-row"
                  key={`brokerage-${title}`}>
                  <PaperlessPreferencesAccountGroup
                    title={title}
                    isEmailDelivery={paperlessDelivery[serviceTypeId]}
                    disabledRadioGroup={
                      isSalesforceRepEnabled &&
                      !loadedPaperlessDelivery[serviceTypeId]
                    }
                    handleChange={updateDeliverySelection}
                    note={hasOwnedMutualFunds && note}
                    product={'brokerage'}
                    serviceTypeId={serviceTypeId}
                    modalContent={modalContent}
                    showBanner={showBanner}
                  />
                </div>
              );
            }
          )}
          {!hasOwnedMutualFunds && renderCheckbox()}
        </PaperlessPreferenceSection>
      )}
      {hasNationalSavingsPreferences && (
        <PaperlessPreferenceSection
          accountsSectionTitle={'T. Rowe Price College Savings Plan Accounts'}>
          {paperlessPreferencesSettings.nationalsavings.map(
            ({ modalContent, note, serviceTypeId, showBanner, title }) => {
              return (
                <div
                  className="paperless-account-section-row"
                  key={`national-${title}`}>
                  <PaperlessPreferencesAccountGroup
                    title={title}
                    isEmailDelivery={paperlessDelivery[serviceTypeId]}
                    disabledRadioGroup={
                      isSalesforceRepEnabled &&
                      !loadedPaperlessDelivery[serviceTypeId]
                    }
                    handleChange={updateDeliverySelection}
                    note={note}
                    product={'national'}
                    serviceTypeId={serviceTypeId}
                    modalContent={modalContent}
                    showBanner={showBanner}
                  />
                </div>
              );
            }
          )}
        </PaperlessPreferenceSection>
      )}
      {hasMarylandSavingsPreferences && (
        <PaperlessPreferenceSection
          accountsSectionTitle={
            'Maryland Senator Edward J. Kasemeyer College Investment Plan Accounts'
          }>
          {paperlessPreferencesSettings.marylandsavings.map(
            ({ modalContent, note, serviceTypeId, showBanner, title }) => {
              return (
                <div
                  className="paperless-account-section-row"
                  key={`maryland-${title}`}>
                  <PaperlessPreferencesAccountGroup
                    title={title}
                    isEmailDelivery={paperlessDelivery[serviceTypeId]}
                    disabledRadioGroup={
                      isSalesforceRepEnabled &&
                      !loadedPaperlessDelivery[serviceTypeId]
                    }
                    handleChange={updateDeliverySelection}
                    note={note}
                    product={'maryland'}
                    serviceTypeId={serviceTypeId}
                    modalContent={modalContent}
                    showBanner={showBanner}
                  />
                </div>
              );
            }
          )}
        </PaperlessPreferenceSection>
      )}
      {hasAlaskaSavingsPreferences && (
        <PaperlessPreferenceSection
          accountsSectionTitle={'Alaska 529 Accounts'}>
          {paperlessPreferencesSettings.alaskasavings.map(
            ({ modalContent, note, serviceTypeId, showBanner, title }) => {
              return (
                <div
                  className="paperless-account-section-row"
                  key={`alaska-${title}`}>
                  <PaperlessPreferencesAccountGroup
                    title={title}
                    isEmailDelivery={paperlessDelivery[serviceTypeId]}
                    disabledRadioGroup={
                      isSalesforceRepEnabled &&
                      !loadedPaperlessDelivery[serviceTypeId]
                    }
                    handleChange={updateDeliverySelection}
                    note={note}
                    product={'alaska'}
                    serviceTypeId={serviceTypeId}
                    modalContent={modalContent}
                    showBanner={showBanner}
                  />
                </div>
              );
            }
          )}
        </PaperlessPreferenceSection>
      )}
      {hasAdvisoryPreferences && (
        <PaperlessPreferenceSection
          accountsSectionTitle={
            <div>
              T. Rowe Price Retirement Advisory Service
              <TradeMark className="tradeMark" />
            </div>
          }>
          {paperlessPreferencesSettings.activeadvisory.map(
            ({ modalContent, note, serviceTypeId, showBanner, title }) => {
              return (
                <div
                  className="paperless-account-section-row"
                  key={`activeadvisory-${title}`}>
                  <PaperlessPreferencesAccountGroup
                    title={title}
                    isEmailDelivery={true}
                    handleChange={updateDeliverySelection}
                    note={note}
                    product={'activeadvisory'}
                    serviceTypeId={serviceTypeId}
                    modalContent={modalContent}
                    showBanner={showBanner}
                    disabledRadioGroup={true}
                  />
                </div>
              );
            }
          )}
        </PaperlessPreferenceSection>
      )}
      {hasActivePlusPreferences && (
        <PaperlessPreferenceSection
          accountsSectionTitle={
            <Fragment>
              T. Rowe Price ActivePlus Portfolios
              <Registered />
            </Fragment>
          }>
          {paperlessPreferencesSettings.activeplus.map(
            ({ modalContent, note, serviceTypeId, showBanner, title }) => {
              return (
                <div
                  className="paperless-account-section-row"
                  key={`activeplus-${title}`}>
                  <PaperlessPreferencesAccountGroup
                    title={title}
                    isEmailDelivery={true}
                    handleChange={updateDeliverySelection}
                    note={note}
                    product={'activeplus'}
                    serviceTypeId={serviceTypeId}
                    modalContent={modalContent}
                    showBanner={showBanner}
                    disabledRadioGroup={true}
                  />
                </div>
              );
            }
          )}
        </PaperlessPreferenceSection>
      )}
    </PreferencesRow>
  );

  const HeaderFootNote = () => (
    <div className="paperless-accordion-footnote">
      <strong>Please Note: </strong>
      <span>
        {hasOwnedCollegeSavingsOnly
          ? Content.PaperlessPreferencesTopFootNote529Only
          : Content.PaperlessPreferencesTopFootNote}
      </span>
    </div>
  );
  const FooterFootNote = () => (
    <div className="paperless-accordion-footnote footerText">
      <span>
        *An account service fee of $20 will be charged annually for each T. Rowe
        Price mutual fund account with a balance below $10,000 unless you meet
        criteria for a fee waiver.{' '}
        <a rel="noreferrer" className="footerLink" href={feesAndMinimumsUrl} target="_blank" >
          Learn more
        </a>{' '}
        about this account service fee, including other ways to waive the fee.
      </span>
    </div>
  );

  return (
    <Fragment>
      <div className="paperless-preferences-page">
        <ConsentModal
          onCancel={() => {
            setShowConsentModal(false);
            if(!hideConsentActionZone) {
              setPaperlessDelivery(loadedPaperlessDelivery);
            }
          }}
          requestStatus={requestStatus}
          onSave={handleSave}
          termsContent={termsContent}
          hideConsentActionZone={hideConsentActionZone}
          showConsentModal={showConsentModal}
        />
        {!isSalesforceRepEnabled && (
          <PreferencesHeader>
            <div className="paperless-preferences-header">
              Paperless Preferences
            </div>
          </PreferencesHeader>
        )}
        {hasInvalidOrMissingEmail ? (
          <PreferencesSection className="paperless-preferences-main-section">
            <PreferencesInvalidEmail />
          </PreferencesSection>
        ) : (
          paperlessDelivery && (
            <PreferencesSection className="paperless-preferences-main-section">
              {!isSalesforceRepEnabled ? (
                <Fragment>
                  <PaperlessPreferencesOverview
                    selectAllEDelivery={switchToAllEPaperless}
                    setShowPaperlessTerms={() => {
                      setShowConsentModal(true);
                      setHideConsentActionZone(true);
                    }}
                    paperlessPreferencesStatus={paperlessPreferencesStatus}
                  />
                  <PaperlessAccordionWithFootNotes
                    openTitle={'View and customize paperless preferences'}
                    closeTitle={'Hide paperless preferences'}
                    id={'accordion-header-paperless'}
                    body={<PaperlessPreferencesViewSection />}
                    headerNotes={<HeaderFootNote />}
                    requestStatus={requestStatus}
                  />
                </Fragment>
              ) : (
                <Fragment>
                  <HeaderFootNote />
                  <PaperlessPreferencesViewSection />
                </Fragment>
              )}
              {hasMutualFundPreferences
              && <FooterFootNote />}
              <FixedSubmitFooter
                cancel={() => handleCancel()}
                requestStatusKey="updateDocumentDelivery"
                shouldShow={!hideFixedFooter}
                shouldDisableSubmit={checkIfDisableSubmit()}
                submit={handleSave}
              />
            </PreferencesSection>
          )
        )}
      </div>
    </Fragment>
  );
};

PaperlessPreferences.prototypes = {
  requestStatus: object.isRequired,
  hasOwnedBrokerage: bool.isRequired,
  history: object.isRequired,
  hasOwnedMutualFunds: bool.isRequired
};

const mapStateToProps = (state) => ({
  requestStatus: getRequestStatus(state),
  isSalesforceRepEnabled: isSalesforceRepEnabled(state),
  documentDelivery: getUpdatedDocumentDeliveryEnrollments(state),
  router: state.router,
  hasInvalidOrMissingEmail: getHasInvalidOrNoEmailAddress(state),
  paperlessPreferencesSettings: getPaperlessPreferences(state),
  hasOwnedBrokerage: hasOwnedBrokerage(state),
  hasOwnedMutualFunds: hasOwnedMutualFunds(state),
  hideFixedFooter: state.hideFixedFooterSaveOptions,
  eligibleMFServiceTypeIds: getEligibleServiceTypeIds(state),
  paperlessPreferencesStatus: getPaperlessPreferencesOverview(state),
  emailAddress: getPrimaryEmailAddress(state),
  hasOwnedCollegeSavingsOnly: getHasOwnedCollegeSavingsOnly(state)
});

export default withRouter(connect(mapStateToProps)(PaperlessPreferences));
